import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 45000;

const PRIORITY = {
  info: { label: 'Announcement', accent: '#002FA7', bar: 'bg-[#002FA7]', chip: 'bg-blue-50 text-[#002FA7]', Icon: Info },
  warning: { label: 'Important', accent: '#B45309', bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800', Icon: AlertTriangle },
  urgent: { label: 'Urgent', accent: '#B91C1C', bar: 'bg-red-600', chip: 'bg-red-50 text-red-700', Icon: AlertTriangle },
};

// Floating card that surfaces unacknowledged announcements to every recipient
// on any authenticated screen — the "broadcast to everyone at once" experience.
const AnnouncementBroadcastCard = () => {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const dismissed = useRef(new Set()); // ids snoozed for this session
  const navigate = useNavigate();

  const fetchMine = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/announcements/mine`, { withCredentials: true });
      const pending = (r.data || []).filter((a) => !a.acknowledged && !dismissed.current.has(a.id));
      // Urgent first, then newest
      pending.sort((a, b) => {
        if (a.priority === 'urgent' && b.priority !== 'urgent') return -1;
        if (b.priority === 'urgent' && a.priority !== 'urgent') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setQueue(pending);
    } catch {
      /* not logged in / no company — stay silent */
    }
  }, []);

  useEffect(() => {
    fetchMine();
    const t = setInterval(fetchMine, POLL_MS);
    return () => clearInterval(t);
  }, [fetchMine]);

  const current = queue[0];
  if (!current) return null;

  const meta = PRIORITY[current.priority] || PRIORITY.info;
  const { Icon } = meta;

  const acknowledge = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/announcements/${current.id}/acknowledge`, {}, { withCredentials: true });
      dismissed.current.add(current.id);
      setQueue((q) => q.filter((a) => a.id !== current.id));
    } catch {
      toast.error('Could not acknowledge — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const snooze = () => {
    dismissed.current.add(current.id);
    setQueue((q) => q.filter((a) => a.id !== current.id));
  };

  return (
    <div
      className="fixed z-[9999] bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] animate-[slideUp_0.25s_ease-out]"
      data-testid="announcement-broadcast-card"
      role="alertdialog"
      aria-live="assertive"
    >
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div className="relative rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className={`h-1.5 w-full ${meta.bar}`} />
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${meta.accent}14`, color: meta.accent }}
            >
              <Megaphone className="w-4.5 h-4.5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${meta.chip}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                {queue.length > 1 && (
                  <span className="text-[10px] text-slate-400 font-semibold" data-testid="announcement-queue-count">
                    1 of {queue.length}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-bold text-slate-900 mt-1.5 leading-snug" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {current.title}
              </h3>
              <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">
                {current.body}
              </p>
              <p className="text-[10px] text-slate-400 mt-2">{new Date(current.created_at).toLocaleString()}</p>
            </div>
            <button
              onClick={snooze}
              data-testid="announcement-snooze"
              aria-label="Dismiss for now"
              className="shrink-0 text-slate-400 hover:text-slate-700 -mt-1 -mr-1 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
            <button
              onClick={() => { navigate('/my-announcements'); snooze(); }}
              data-testid="announcement-view-all"
              className="text-xs font-semibold text-[#002FA7] hover:underline"
            >
              View all
            </button>
            <button
              onClick={acknowledge}
              disabled={busy}
              data-testid="announcement-acknowledge"
              className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white text-xs font-bold rounded-lg px-4 py-2 hover:bg-[#00247D] disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBroadcastCard;
